import { V_EDITOR_INSTRUCTIONS } from 'consts'
import { TAiVideoTimelineData } from 'types/aiVideo'

export type TVEditorInstructionAction = 'add-to-timeline'
export type TVEditorInstructionPayloadType = 'ai-video'

/**
 * Envelope the editor accepts via the `v-editor-instructions` localStorage bridge.
 * The sender owns producing `data` in the shape the editor consumes; the editor only
 * validates this envelope and stays blind to who wrote it.
 */
export interface IVEditorInstructionPayload {
  action: TVEditorInstructionAction
  type: TVEditorInstructionPayloadType
  userId?: number | null
  data: TAiVideoTimelineData
}

const isVEditorInstructionPayload = (
  val: unknown
): val is IVEditorInstructionPayload => {
  if (!val || typeof val !== 'object') return false
  const o = val as Record<string, unknown>
  return (
    o.action === 'add-to-timeline' &&
    o.type === 'ai-video' &&
    typeof o.data === 'object' &&
    o.data !== null
  )
}

/** Clears bridge key (after apply or deliberate discard). */
export function clearVEditorInstructions(): void {
  localStorage.removeItem(V_EDITOR_INSTRUCTIONS)
}

/**
 * Parses the localStorage bridge payload without removing valid entries.
 * Removes the key only for corrupt JSON or an invalid envelope (won't retry junk).
 */
export function readVEditorInstructionsPayload(): IVEditorInstructionPayload | null {
  const raw = localStorage.getItem(V_EDITOR_INSTRUCTIONS)
  if (!raw) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    clearVEditorInstructions()
    return null
  }

  if (!isVEditorInstructionPayload(parsed)) {
    clearVEditorInstructions()
    return null
  }

  return parsed
}
